// ═══════════════════════════════════════════════════════════════════════════
// Per-action view+space translators (CC-2c sub-arm 3) — PACKAGE-INTERNAL
// ═══════════════════════════════════════════════════════════════════════════
//
// The thin, total per-action translators behind the VIEW+SPACE domain
// (CREATE_VIEWPORT/SWITCH_TO_MODEL_SPACE/SWITCH_TO_PAPER_SPACE/RENAME_LAYOUT).
// Each AI shape maps 1:1 onto the corresponding contract `EditorCommand`, with
// no simplification — full required params per the EditorCommand + ТЗ.
//
// IMPORTANT (AI safe contour): these translators produce view/space-mutating
// `EditorCommand`s WITHOUT running the CC-2a fail-closed guard
// (`assertAiCallableViewSpaceAction`). They are therefore PACKAGE-INTERNAL: this
// file is NOT re-exported by the barrel (`index.ts`). The ONLY public
// view+space entry is the guarded `translateViewSpaceCommand` in
// `viewSpaceDispatcher.ts` (which imports these). This mirrors the BLOCK domain
// (per-action translators in non-barrelled `blockDispatcher*.ts`), the
// ENTITY-CREATION domain (`entityTranslators.ts`) and the DRAW-MODIFY domain
// (`drawModifyTranslators.ts`) — so a consumer of `@dxf-ai-editor/ai-commands`
// can never emit `geometry/create-viewport` / `shell/set-layout-context` /
// `shell/rename-space` past the fail-closed guard. Honours the CLAUDE.md rules:
//   "Model/Layout/Viewport — это часть модели, а не только UI." +
//   "AI drafting … может только предлагать и исполнять команды через
//    безопасный контур."

import type { EditorCommand } from "@dxf-ai-editor/contracts";

import { AI_COMMAND_SOURCE, type AiDispatchContext } from "./blockDispatcherShared";
import {
  ViewSpaceContextError,
  assertPaperSpaceLayoutTarget,
  resolveActivationLayoutId,
  resolveAiCurrentColor,
} from "./viewSpaceGuard";
import type {
  CreateViewportAiCommand,
  RenameLayoutAiCommand,
  SetCurrentColorAiCommand,
  SetLayoutAiCommand,
  SwitchToModelSpaceAiCommand,
  SwitchToPaperSpaceAiCommand,
} from "./viewSpaceSchemas";

// ─── CREATE_VIEWPORT (MVIEW) ───────────────────────────────────────
//
// Emits `geometry/create-viewport`. `modelExtent` is forwarded only when the AI
// supplied it (MVIEW FIT); when absent the reducer applies its viewHeight=100,
// center=(0,0) fallback (contract parity) — so we must NOT inject a default.
//
// MVIEW is LAYOUT-ONLY in AutoCAD: a viewport always belongs to a paper-space
// layout, never model space. When the context supplies the layout list we
// validate `spaceId` is a real paper-space layout and FAIL CLOSED on model /
// orphan ids (the reducer applies the same guard as a defence-in-depth safety
// net for every other dispatch path). When the list is absent (`undefined`) the
// translator-level check is skipped and the reducer alone enforces it.
export function translateCreateViewport(
  command: CreateViewportAiCommand,
  context: AiDispatchContext = {},
): EditorCommand[] {
  assertPaperSpaceLayoutTarget(command.spaceId, context);
  return [
    {
      type: "geometry/create-viewport",
      source: AI_COMMAND_SOURCE,
      spaceId: command.spaceId,
      corner1: { x: command.corner1.x, y: command.corner1.y },
      corner2: { x: command.corner2.x, y: command.corner2.y },
      ...(command.modelExtent !== undefined
        ? {
            modelExtent: {
              minX: command.modelExtent.minX,
              minY: command.modelExtent.minY,
              maxX: command.modelExtent.maxX,
              maxY: command.modelExtent.maxY,
            },
          }
        : {}),
    },
  ];
}

// ─── MSPACE / PSPACE shared activation ─────────────────────────────
//
// MSPACE and PSPACE differ ONLY in the target `layoutContext` ("viewport" vs
// "paper"). Both mirror the CLI MSPACE/PSPACE (editorStore): move model→first-
// layout (or stay on the active layout) and set the layout context TOGETHER, so
// `activeSpaceId` and `layoutContext` are always consistent. The AI translator
// emits `shell/activate-space` — the canonical activation command the UI tab
// strip dispatches too — which sets BOTH fields in one reducer pass. The target
// layout is resolved from context; with no paper-space layout available we FAIL
// CLOSED (ViewSpaceContextError) rather than emit an inconsistent context-only
// switch. Single source of truth for both actions (avoids a copy-paste clone).
function activateResolvedLayout(
  context: AiDispatchContext,
  layoutContext: "viewport" | "paper",
): EditorCommand[] {
  const layoutId = resolveActivationLayoutId(context);
  if (layoutId === null) {
    throw new ViewSpaceContextError("no-active-layout");
  }
  return [
    {
      type: "shell/activate-space",
      source: AI_COMMAND_SOURCE,
      spaceId: layoutId,
      layoutContext,
    },
  ];
}

// ─── SWITCH_TO_MODEL_SPACE (MSPACE) ────────────────────────────────
//
// Enters floating model space inside a viewport on a paper-space layout →
// `shell/activate-space` with `layoutContext: "viewport"`.
export function translateSwitchToModelSpace(
  _command: SwitchToModelSpaceAiCommand,
  context: AiDispatchContext = {},
): EditorCommand[] {
  return activateResolvedLayout(context, "viewport");
}

// ─── SWITCH_TO_PAPER_SPACE (PSPACE) ────────────────────────────────
//
// Returns a paper-space layout to paper context (inverse of MSPACE) →
// `shell/activate-space` with `layoutContext: "paper"`.
export function translateSwitchToPaperSpace(
  _command: SwitchToPaperSpaceAiCommand,
  context: AiDispatchContext = {},
): EditorCommand[] {
  return activateResolvedLayout(context, "paper");
}

// ─── SET_LAYOUT (LAYOUT SET / LAYOUT S) ────────────────────────────
//
// Makes a paper-space layout the current/active space by id → the CANONICAL
// `shell/activate-space` with `layoutContext: "paper"` (the same command the UI
// tab strip dispatches, so `activeSpaceId` + `layoutContext` stay consistent —
// single source of truth, no parallel activation path). LAYOUT Set is layout-
// only: when the context supplies the paper-space layout list we validate the
// target and FAIL CLOSED on model / orphan ids (the Model tab is not selectable
// via LAYOUT Set — AutoCAD parity); when the list is absent the reducer remains
// the safety net. The reducer's activate-space path is idempotent for the
// already-current layout.
export function translateSetLayout(
  command: SetLayoutAiCommand,
  context: AiDispatchContext = {},
): EditorCommand[] {
  assertPaperSpaceLayoutTarget(command.spaceId, context);
  return [
    {
      type: "shell/activate-space",
      source: AI_COMMAND_SOURCE,
      spaceId: command.spaceId,
      layoutContext: "paper",
    },
  ];
}

// ─── RENAME_LAYOUT (LAYOUT R / LAYOUT RENAME) ──────────────────────
//
// Renames a layout tab by id → `shell/rename-space`. The reducer enforces the
// AutoCAD-parity guards (Model space not renamable, duplicate/empty rejected).
export function translateRenameLayout(
  command: RenameLayoutAiCommand,
): EditorCommand[] {
  return [
    {
      type: "shell/rename-space",
      source: AI_COMMAND_SOURCE,
      spaceId: command.spaceId,
      newName: command.newName,
    },
  ];
}

// ─── SET_CURRENT_COLOR (COLOR — CECOLOR) ───────────────────────────
//
// Sets the CURRENT colour ($CECOLOR) that newly-created entities inherit →
// `header/set-cecolor`. The colour string is STRICTLY validated through the
// one-truth `resolveAiCurrentColor` → `parseColorCommandValueStrict` validator
// (ByLayer / ByBlock / ACI 1..255 / #rrggbb / `R,G,B`) — the SAME strict
// accept-list the human COLOR command enforces (CECOLOR FINDING 2), NOT the
// permissive `coerceColor` migration codec. An out-of-range ACI (`"999"` — would
// export an invalid `$CECOLOR=62=999`) or an unknown keyword (`"blu"` — would
// silently reset to ByLayer) is REJECTED with `AiColorValidationError` BEFORE any
// EditorCommand is produced (fail-closed) — the AI, a less-trusted contour, may
// not bypass the gate the human path enforces. Safe contour: on a VALID value
// this EMITS a command (the reducer writes `doc.cecolor`); it NEVER writes the
// document directly — honouring the CLAUDE.md AI rule. It does NOT modify any
// existing entity; it only changes the current-colour state.
export function translateSetCurrentColor(
  command: SetCurrentColorAiCommand,
): EditorCommand[] {
  return [
    {
      type: "header/set-cecolor",
      source: AI_COMMAND_SOURCE,
      color: resolveAiCurrentColor(command.color),
    },
  ];
}
